<template>
  <div class="statement-page-wrapper">
    <vue-element-loading
      :active="appLoading"
      :is-full-screen="true"
      background-color="#FFFFFF"
      color="#9a6b27"
      spinner="bar-fade-scale"
    />
    <ServerError v-if="ServerError" />

    <v-snackbar v-model="showsnackbar" color="#1e293b" right :timeout="timeout">
      <div class="d-flex align-center justify-space-between text-white" style="font-size: 13px;">
        <span>{{ msg }}</span>
        <v-btn small icon text @click="showsnackbar = false" class="ml-2">
          <v-icon small color="white">mdi-close</v-icon>
        </v-btn>
      </div>
    </v-snackbar>

    <!-- Top Executive Header & Navigation Card (Hidden in Print) -->
    <v-card flat class="statement-header-card pa-4 mb-4 rounded-lg no-print">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 16px;">
        <!-- Left: Back Navigation & Title -->
        <div class="d-flex align-center flex-wrap" style="gap: 12px;">
          <v-btn
            small
            outlined
            color="#9a6b27"
            class="back-nav-btn text-capitalize"
            @click="goBack()"
          >
            <v-icon left small>mdi-arrow-left</v-icon>
            Back to Customers
          </v-btn>

          <div class="header-divider-v d-none d-sm-block"></div>

          <div class="d-flex align-center">
            <div class="header-icon-box mr-3">
              <v-icon color="#9a6b27" size="22">mdi-book-open-page-variant-outline</v-icon>
            </div>
            <div>
              <div class="page-title">CUSTOMER STATEMENT &amp; LEDGER</div>
              <div class="page-subtitle">Chronological audit trail of all bills, payments, debit charges and credit settlements</div>
            </div>
          </div>
        </div>

        <!-- Right: Action Bar (Print, Export CSV, Refresh) -->
        <div class="d-flex align-center flex-wrap" style="gap: 10px;">
          <v-btn
            outlined
            small
            color="#475569"
            class="action-secondary-btn text-capitalize"
            @click="exportCSV()"
            :disabled="!entries || entries.length === 0"
          >
            <v-icon left small color="#475569">mdi-file-delimited-outline</v-icon>
            Export CSV
          </v-btn>

          <v-btn
            dark
            small
            color="#9a6b27"
            class="action-primary-btn text-capitalize elevation-1"
            @click="printStatement()"
          >
            <v-icon left small>mdi-printer</v-icon>
            Print Statement
          </v-btn>

          <v-btn
            icon
            small
            title="Refresh Statement"
            class="refresh-icon-btn"
            @click="getStatement()"
          >
            <v-icon small color="#64748b">mdi-refresh</v-icon>
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Customer Identity & Account Hero Card -->
    <v-card flat class="customer-hero-card pa-4 mb-4 rounded-lg">
      <div class="d-flex flex-wrap align-center justify-space-between" style="gap: 20px;">
        <!-- Left: Customer Avatar & Contact Details -->
        <div class="d-flex align-center flex-grow-1">
          <div class="customer-hero-avatar mr-4">
            {{ getInitials(customer ? customer.customerName : 'CU') }}
          </div>
          <div class="hero-customer-info">
            <div class="d-flex align-center flex-wrap" style="gap: 8px;">
              <h2 class="hero-customer-name">
                {{ (customer && customer.customerName) ? customer.customerName : 'Customer Account' }}
              </h2>
              <span class="customer-status-pill">Active Account</span>
              <span v-if="customer && (customer.panNo || customer.aadharNo)" class="kyc-verified-pill">
                <v-icon size="12" color="#059669" class="mr-1">mdi-shield-check</v-icon>
                KYC On File
              </span>
            </div>

            <!-- Contact & Location Metadata Row -->
            <div class="d-flex flex-wrap align-center hero-meta-row mt-2" style="gap: 16px;">
              <div class="hero-meta-item" v-if="customer && customer.mobileNumber">
                <v-icon size="14" color="#64748b" class="mr-1">mdi-phone-outline</v-icon>
                <span>{{ customer.mobileNumber }}</span>
              </div>
              <div class="hero-meta-item" v-if="customer && customer.email">
                <v-icon size="14" color="#64748b" class="mr-1">mdi-email-outline</v-icon>
                <span>{{ customer.email }}</span>
              </div>
              <div class="hero-meta-item" v-if="customer && (customer.address || customer.city)">
                <v-icon size="14" color="#64748b" class="mr-1">mdi-map-marker-outline</v-icon>
                <span>{{ customer.address ? customer.address : '' }}{{ customer.city ? (customer.address ? ', ' : '') + customer.city : '' }}</span>
              </div>
              <div class="hero-meta-item" v-if="customer && customer.panNo">
                <v-icon size="14" color="#64748b" class="mr-1">mdi-card-account-details-outline</v-icon>
                <span>PAN: <strong>{{ customer.panNo }}</strong></span>
              </div>
              <div class="hero-meta-item" v-if="customer && customer.aadharNo">
                <v-icon size="14" color="#64748b" class="mr-1">mdi-card-bulleted-outline</v-icon>
                <span>Aadhaar: <strong>{{ customer.aadharNo }}</strong></span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right: Ledger Account Standing Pill & Timestamp -->
        <div class="hero-right-status text-right d-none d-sm-flex flex-column align-end">
          <div
            class="standing-badge"
            :class="customerBalanceAmount > 0 ? 'standing-due' : 'standing-settled'"
          >
            <v-icon size="14" :color="customerBalanceAmount > 0 ? '#ea580c' : '#059669'" class="mr-1">
              {{ customerBalanceAmount > 0 ? 'mdi-alert-circle-outline' : 'mdi-check-decagram' }}
            </v-icon>
            {{ customerBalanceAmount > 0 ? 'Receivable Outstanding' : 'Account Settled / In Good Standing' }}
          </div>
          <div class="statement-timestamp mt-1">
            Statement generated as of {{ todayFormatted }}
          </div>
        </div>
      </div>
    </v-card>

    <!-- 4 KPI Overview Metric Cards -->
    <div class="kpi-grid mb-4">
      <!-- 1. Current Net Balance -->
      <div class="kpi-card" :class="{ 'kpi-highlight-due': customerBalanceAmount > 0 }">
        <div class="kpi-content">
          <div class="kpi-label">CURRENT BALANCE</div>
          <div class="kpi-value" :class="customerBalanceAmount > 0 ? 'text-amber-gold' : 'text-emerald-green'">
            ₹{{ formatCurrency(customerBalanceAmount) }}
          </div>
          <div class="kpi-hint">
            {{ customerBalanceAmount > 0 ? 'Net amount due from customer' : 'All accounts settled in full' }}
          </div>
        </div>
        <div class="kpi-icon-pill" :style="customerBalanceAmount > 0 ? 'background: #fff7ed; color: #ea580c;' : 'background: #ecfdf5; color: #059669;'">
          <v-icon size="22" :color="customerBalanceAmount > 0 ? '#ea580c' : '#059669'">mdi-scale-balance</v-icon>
        </div>
      </div>

      <!-- 2. Total Billed Charges -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL INVOICED (DEBIT)</div>
          <div class="kpi-value">₹{{ formatCurrency(totalBilledAmount) }}</div>
          <div class="kpi-hint">{{ totalBillsCount }} invoice bills issued</div>
        </div>
        <div class="kpi-icon-pill" style="background: #eff6ff; color: #2563eb;">
          <v-icon size="22" color="#2563eb">mdi-receipt-text-outline</v-icon>
        </div>
      </div>

      <!-- 3. Total Payments Recorded -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">TOTAL PAID (CREDIT)</div>
          <div class="kpi-value text-emerald-green">₹{{ formatCurrency(totalPaidAmount) }}</div>
          <div class="kpi-hint">{{ totalPaymentsCount }} payment receipts recorded</div>
        </div>
        <div class="kpi-icon-pill" style="background: #ecfdf5; color: #059669;">
          <v-icon size="22" color="#059669">mdi-cash-check</v-icon>
        </div>
      </div>

      <!-- 4. Credit Limit & Advance Credit -->
      <div class="kpi-card">
        <div class="kpi-content">
          <div class="kpi-label">CREDIT LIMIT &amp; ADVANCE</div>
          <div class="kpi-value">
            <span v-if="creditLimit > 0">₹{{ formatCurrency(creditLimit) }}</span>
            <span v-else class="text-muted" style="font-size: 16px; font-weight: 600;">No Limit Set</span>
          </div>
          <div class="kpi-hint">
            <span v-if="customer && customer.advanceAmount > 0" class="text-emerald-green font-weight-bold">
              ₹{{ formatCurrency(customer.advanceAmount) }} advance held
            </span>
            <span v-else>No advance credit held</span>
          </div>
        </div>
        <div class="kpi-icon-pill" style="background: #fdf4ff; color: #a855f7;">
          <v-icon size="22" color="#a855f7">mdi-shield-check-outline</v-icon>
        </div>
      </div>
    </div>

    <!-- Print-Only Official Statement Header -->
    <div class="print-only print-statement-header mb-6">
      <div class="d-flex justify-space-between align-start pb-4 border-b-2">
        <div>
          <h1 class="print-brand-title">JEWEL<span style="color: #9a6b27;">BIZ</span></h1>
          <div class="print-brand-tagline">BRILLIANCE IN BUSINESS &bull; OFFICIAL STATEMENT OF ACCOUNT</div>
        </div>
        <div class="text-right">
          <div class="print-doc-title">CUSTOMER STATEMENT</div>
          <div class="print-doc-date">Date: {{ todayFormatted }}</div>
          <div class="print-doc-period">Customer ID: {{ customer ? customer._id : '—' }}</div>
        </div>
      </div>

      <div class="print-customer-box mt-4 pa-3">
        <div class="d-flex justify-space-between">
          <div>
            <div class="print-label">CUSTOMER NAME &amp; DETAILS</div>
            <div class="print-val-strong">{{ customer ? customer.customerName : '—' }}</div>
            <div class="print-val-sub">{{ customer ? customer.address : '' }}{{ customer && customer.city ? ', ' + customer.city : '' }}</div>
            <div class="print-val-sub">Phone: {{ customer ? customer.mobileNumber : '—' }} | Email: {{ customer && customer.email ? customer.email : '—' }}</div>
          </div>
          <div class="text-right">
            <div class="print-label">FINANCIAL SUMMARY</div>
            <div class="print-val-sub">Total Billed: <strong>₹{{ formatCurrency(totalBilledAmount) }}</strong></div>
            <div class="print-val-sub">Total Paid: <strong>₹{{ formatCurrency(totalPaidAmount) }}</strong></div>
            <div class="print-val-strong mt-1" style="font-size: 15px; color: #78350f;">
              Closing Balance: ₹{{ formatCurrency(customerBalanceAmount) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Master Statement Content Card -->
    <v-card flat class="statement-table-card rounded-lg overflow-hidden">
      <!-- Interactive Filter & Search Bar (Hidden in Print) -->
      <div class="table-toolbar pa-4 d-flex flex-wrap align-center justify-space-between no-print" style="gap: 12px;">
        <!-- Filter Tabs / Pills -->
        <div class="d-flex align-center flex-wrap" style="gap: 6px;">
          <button
            type="button"
            class="filter-pill-btn"
            :class="{ active: activeFilter === 'all' }"
            @click="activeFilter = 'all'"
          >
            All Transactions ({{ entries.length }})
          </button>
          <button
            type="button"
            class="filter-pill-btn"
            :class="{ active: activeFilter === 'bills' }"
            @click="activeFilter = 'bills'"
          >
            <v-icon left size="14" :color="activeFilter === 'bills' ? '#9a6b27' : '#64748b'">mdi-receipt-text-outline</v-icon>
            Invoices / Bills ({{ totalBillsCount }})
          </button>
          <button
            type="button"
            class="filter-pill-btn"
            :class="{ active: activeFilter === 'payments' }"
            @click="activeFilter = 'payments'"
          >
            <v-icon left size="14" :color="activeFilter === 'payments' ? '#9a6b27' : '#64748b'">mdi-cash-check</v-icon>
            Payments ({{ totalPaymentsCount }})
          </button>
          <button
            type="button"
            class="filter-pill-btn"
            :class="{ active: activeFilter === 'pending' }"
            @click="activeFilter = 'pending'"
          >
            <v-icon left size="14" :color="activeFilter === 'pending' ? '#9a6b27' : '#64748b'">mdi-alert-circle-outline</v-icon>
            Pending Dues ({{ pendingBillsCount }})
          </button>
        </div>

        <!-- Search & Sort Controls -->
        <div class="d-flex align-center flex-wrap" style="gap: 10px;">
          <div class="statement-search-wrap">
            <v-text-field
              v-model="searchQuery"
              clearable
              dense
              outlined
              hide-details
              placeholder="Search by Bill No, Date, Amount..."
              prepend-inner-icon="mdi-magnify"
              class="statement-search-input"
            ></v-text-field>
          </div>

          <v-btn
            small
            outlined
            color="#475569"
            class="sort-toggle-btn text-capitalize"
            @click="toggleSortOrder()"
            :title="sortOrder === 'asc' ? 'Switch to newest first' : 'Switch to oldest first (Ledger)'"
          >
            <v-icon left small>
              {{ sortOrder === 'asc' ? 'mdi-sort-clock-ascending-outline' : 'mdi-sort-clock-descending-outline' }}
            </v-icon>
            {{ sortOrder === 'asc' ? 'Oldest First' : 'Newest First' }}
          </v-btn>
        </div>
      </div>

      <!-- Ledger Table -->
      <div v-if="filteredEntries && filteredEntries.length > 0">
        <v-simple-table class="statement-ledger-table">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left" style="width: 50px;">#</th>
                <th class="text-left" style="min-width: 140px;">Transaction Date</th>
                <th class="text-left" style="min-width: 160px;">Transaction Type</th>
                <th class="text-left" style="min-width: 140px;">Bill Reference</th>
                <th class="text-right" style="min-width: 130px;">Debit (Billed)</th>
                <th class="text-right" style="min-width: 130px;">Credit (Paid)</th>
                <th class="text-right" style="min-width: 130px;">Bill Due / Pending</th>
                <th class="text-right" style="min-width: 140px;">Running Balance</th>
                <th class="text-center no-print" style="width: 90px;">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, idx) in filteredEntries"
                :key="'txn-' + idx"
                class="statement-row"
                :class="{ 'row-bill': item.type === 'bill', 'row-payment': item.type === 'payment' }"
              >
                <!-- 1. Index -->
                <td class="text-muted font-weight-medium">
                  {{ idx + 1 }}
                </td>

                <!-- 2. Date -->
                <td>
                  <div class="date-main">{{ formatDate(item.date) }}</div>
                  <div class="date-sub">{{ formatDay(item.date) }}</div>
                </td>

                <!-- 3. Transaction Type Badge -->
                <td>
                  <span
                    v-if="item.type === 'bill'"
                    class="txn-type-badge type-bill"
                  >
                    <v-icon size="13" color="#b45309" class="mr-1">mdi-file-document-outline</v-icon>
                    Invoice (Charge)
                  </span>
                  <span
                    v-else
                    class="txn-type-badge type-payment"
                  >
                    <v-icon size="13" color="#047857" class="mr-1">mdi-cash-fast</v-icon>
                    Payment Received
                  </span>
                </td>

                <!-- 4. Bill Reference -->
                <td>
                  <div v-if="item.billNo" class="d-inline-flex align-center">
                    <router-link
                      v-if="item.salesId"
                      :to="'/billDetail?id=' + item.salesId"
                      class="bill-ref-link"
                      title="View Bill Details"
                    >
                      <v-icon size="13" color="#9a6b27" class="mr-1">mdi-receipt-outline</v-icon>
                      {{ item.billNo }}
                      <v-icon size="11" color="#9a6b27" class="ml-1 no-print">mdi-open-in-new</v-icon>
                    </router-link>
                    <span v-else class="bill-ref-plain font-weight-bold">
                      {{ item.billNo }}
                    </span>
                  </div>
                  <span v-else class="text-muted">—</span>
                </td>

                <!-- 5. Debit (Charge/Bill Amount) -->
                <td class="text-right">
                  <span v-if="item.type === 'bill'" class="amount-debit font-weight-bold">
                    +₹{{ formatCurrency(item.amount) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>

                <!-- 6. Credit (Payment Amount) -->
                <td class="text-right">
                  <span v-if="item.type === 'payment'" class="amount-credit font-weight-bold">
                    -₹{{ formatCurrency(item.amount) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>

                <!-- 7. Pending / Due for Bill -->
                <td class="text-right">
                  <template v-if="item.type === 'bill'">
                    <span
                      v-if="Number(item.pending) > 0"
                      class="bill-pending-badge"
                    >
                      ₹{{ formatCurrency(item.pending) }} due
                    </span>
                    <span v-else class="bill-settled-badge">
                      <v-icon size="12" color="#059669" class="mr-1">mdi-check-circle</v-icon>
                      Fully Settled
                    </span>
                  </template>
                  <template v-else>
                    <span v-if="item.billPendingAmount !== undefined && Number(item.billPendingAmount) > 0" class="text-muted" style="font-size: 11.5px;">
                      ₹{{ formatCurrency(item.billPendingAmount) }} rem.
                    </span>
                    <span v-else class="text-muted">—</span>
                  </template>
                </td>

                <!-- 8. Running Balance -->
                <td class="text-right">
                  <span
                    class="running-balance-text font-weight-bold"
                    :class="item.runningBalance > 0 ? 'balance-positive' : 'balance-zero'"
                  >
                    ₹{{ formatCurrency(item.runningBalance) }}
                  </span>
                </td>

                <!-- 9. Actions (No print) -->
                <td class="text-center no-print">
                  <v-btn
                    v-if="item.salesId"
                    icon
                    small
                    class="table-action-icon-btn"
                    title="View Invoice"
                    @click="viewBill(item.salesId)"
                  >
                    <v-icon small color="#9a6b27">mdi-eye-outline</v-icon>
                  </v-btn>
                  <span v-else class="text-muted">—</span>
                </td>
              </tr>
            </tbody>

            <!-- Table Footer Grand Totals -->
            <tfoot>
              <tr class="statement-total-row">
                <td colspan="4" class="text-left total-label">
                  <div class="d-flex align-center">
                    <v-icon size="16" color="#78350f" class="mr-2">mdi-calculator-variant-outline</v-icon>
                    <strong>STATEMENT TOTALS ({{ filteredEntries.length }} Transactions)</strong>
                  </div>
                </td>
                <td class="text-right font-weight-bold text-amber-gold">
                  +₹{{ formatCurrency(totalBilledAmount) }}
                </td>
                <td class="text-right font-weight-bold text-emerald-green">
                  -₹{{ formatCurrency(totalPaidAmount) }}
                </td>
                <td class="text-right">
                  <span class="text-muted" style="font-size: 11px;">Net Result</span>
                </td>
                <td class="text-right font-weight-bold" style="font-size: 14px; color: #78350f;">
                  ₹{{ formatCurrency(customerBalanceAmount) }}
                </td>
                <td class="no-print"></td>
              </tr>
            </tfoot>
          </template>
        </v-simple-table>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-statement-state text-center py-12 px-4">
        <div class="empty-icon-circle mb-3">
          <v-icon size="36" color="#94a3b8">mdi-file-document-remove-outline</v-icon>
        </div>
        <div class="empty-title">No Statement Records Found</div>
        <div class="empty-subtitle">
          {{ searchQuery || activeFilter !== 'all' ? 'No transactions match your current search and filter criteria.' : 'No invoices, charges or payments have been recorded for this customer yet.' }}
        </div>
        <div class="mt-4" v-if="searchQuery || activeFilter !== 'all'">
          <v-btn
            small
            outlined
            color="#9a6b27"
            class="text-capitalize"
            @click="resetFilters()"
          >
            Clear Search &amp; Filters
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- Print Only Signatures & Verification Strip -->
    <div class="print-only print-footer-strip mt-12 pt-8">
      <div class="d-flex justify-space-between align-end">
        <div class="print-sign-box text-center">
          <div class="print-sign-line"></div>
          <div class="print-sign-label">Customer Signature &amp; Date</div>
        </div>
        <div class="print-sign-box text-center">
          <div class="print-sign-line"></div>
          <div class="print-sign-label">Authorized Signatory &amp; Seal</div>
        </div>
      </div>
      <div class="print-footer-notice text-center mt-6">
        This is a computer-generated statement issued by JewelBiz Management System.
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "CustomerStatement",
  data() {
    return {
      appLoading: false,
      ServerError: false,
      showsnackbar: false,
      timeout: 2500,
      msg: null,

      entries: [],
      customerBalanceAmount: 0,
      creditLimit: 0,
      customer: null,

      // Filter & Search Controls
      searchQuery: "",
      activeFilter: "all", // 'all' | 'bills' | 'payments' | 'pending'
      sortOrder: "asc", // 'asc' (chronological) | 'desc' (newest first)
    };
  },
  computed: {
    todayFormatted() {
      const d = new Date();
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    entriesWithRunningBalance() {
      if (!this.entries || this.entries.length === 0) return [];
      
      // Sort strictly in chronological order for running balance computation
      const sorted = [...this.entries].sort((a, b) => new Date(a.date) - new Date(b.date));
      let currentBal = 0;

      return sorted.map((entry) => {
        if (entry.type === "bill") {
          currentBal += Number(entry.amount) || 0;
        } else if (entry.type === "payment") {
          currentBal -= Number(entry.amount) || 0;
        }
        return {
          ...entry,
          runningBalance: currentBal,
        };
      });
    },
    filteredEntries() {
      let list = [...this.entriesWithRunningBalance];

      // Tab filter
      if (this.activeFilter === "bills") {
        list = list.filter((e) => e.type === "bill");
      } else if (this.activeFilter === "payments") {
        list = list.filter((e) => e.type === "payment");
      } else if (this.activeFilter === "pending") {
        list = list.filter((e) => e.type === "bill" && Number(e.pending) > 0);
      }

      // Search query
      if (this.searchQuery && this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter((e) => {
          const billNo = (e.billNo || "").toLowerCase();
          const type = (e.type || "").toLowerCase();
          const amount = String(e.amount || "");
          const dateFormatted = this.formatDate(e.date).toLowerCase();
          return (
            billNo.includes(q) ||
            type.includes(q) ||
            amount.includes(q) ||
            dateFormatted.includes(q)
          );
        });
      }

      // Sorting
      if (this.sortOrder === "desc") {
        return [...list].sort((a, b) => new Date(b.date) - new Date(a.date));
      } else {
        return [...list].sort((a, b) => new Date(a.date) - new Date(b.date));
      }
    },
    totalBilledAmount() {
      if (!this.entries) return 0;
      return this.entries
        .filter((e) => e.type === "bill")
        .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    },
    totalBillsCount() {
      if (!this.entries) return 0;
      return this.entries.filter((e) => e.type === "bill").length;
    },
    totalPaidAmount() {
      if (!this.entries) return 0;
      return this.entries
        .filter((e) => e.type === "payment")
        .reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
    },
    totalPaymentsCount() {
      if (!this.entries) return 0;
      return this.entries.filter((e) => e.type === "payment").length;
    },
    pendingBillsCount() {
      if (!this.entries) return 0;
      return this.entries.filter((e) => e.type === "bill" && Number(e.pending) > 0).length;
    },
  },
  mounted() {
    this.getStatement();
  },
  methods: {
    getStatement() {
      const customerId = this.$route.query.id;
      if (!customerId) {
        this.msg = "Customer ID missing from request query";
        this.showsnackbar = true;
        return;
      }

      this.appLoading = true;
      axios({
        method: "get",
        url: "/customer/statement",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: customerId,
        },
      })
        .then((response) => {
          this.appLoading = false;
          if (response.data.status === true) {
            this.entries = response.data.entries || [];
            this.customerBalanceAmount = response.data.customerBalanceAmount || 0;
            this.creditLimit = response.data.creditLimit || 0;
            if (response.data.customer) {
              this.customer = response.data.customer;
            } else {
              this.fetchCustomerFallback(customerId);
            }
          } else {
            this.msg = response.data.msg || "Failed to load statement";
            this.showsnackbar = true;
          }
        })
        .catch((err) => {
          this.appLoading = false;
          this.ServerError = true;
          console.error("Statement loading error:", err);
        });
    },
    fetchCustomerFallback(id) {
      axios({
        method: "get",
        url: "/customer/view",
        headers: {
          "x-auth-token": localStorage.getItem("token"),
        },
        params: {
          id: id,
        },
      })
        .then((res) => {
          if (res.data.status && res.data.data) {
            this.customer = res.data.data;
          }
        })
        .catch(() => {});
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push("/CustomerManagement");
      }
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
    },
    resetFilters() {
      this.searchQuery = "";
      this.activeFilter = "all";
      this.sortOrder = "asc";
    },
    viewBill(salesId) {
      if (!salesId) return;
      this.$router.push(`/billDetail?id=${salesId}`);
    },
    printStatement() {
      window.print();
    },
    csvCell(value) {
      const str = value === null || value === undefined ? "" : String(value);
      return `"${str.replace(/"/g, '""')}"`;
    },
    exportCSV() {
      if (!this.filteredEntries || this.filteredEntries.length === 0) return;

      const customerName = this.customer ? this.customer.customerName.replace(/[^a-zA-Z0-9]/g, "_") : "Customer";
      const headers = [
        "S.No",
        "Date",
        "Transaction Type",
        "Bill No",
        "Debit Amount (Rs.)",
        "Credit Amount (Rs.)",
        "Bill Pending (Rs.)",
        "Running Balance (Rs.)",
      ];

      const rows = this.filteredEntries.map((item, idx) => [
        idx + 1,
        this.formatDate(item.date),
        item.type === "bill" ? "Invoice / Bill" : "Payment Received",
        item.billNo || "-",
        item.type === "bill" ? item.amount : 0,
        item.type === "payment" ? item.amount : 0,
        item.type === "bill" ? item.pending : "-",
        item.runningBalance,
      ]);

      const lines = [
        [this.csvCell(`Customer Statement: ${this.customer ? this.customer.customerName : "Account"}`)],
        [this.csvCell(`Generated Date: ${this.todayFormatted}`)],
        [this.csvCell(`Closing Balance: Rs. ${this.formatCurrency(this.customerBalanceAmount)}`)],
        [],
        headers.map(this.csvCell),
        ...rows.map((row) => row.map(this.csvCell)),
      ];

      const csvContent = lines.map((row) => row.join(",")).join("\r\n");

      // BOM so Excel opens the file as UTF-8 instead of mangling special characters
      const blob = new Blob(["﻿" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Statement_${customerName}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    getInitials(name) {
      if (!name) return "CU";
      const parts = name.trim().split(/\s+/);
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return (parts[0][0] + parts[1][0]).toUpperCase();
    },
    formatCurrency(val) {
      if (val === null || val === undefined || isNaN(val)) return "0.00";
      return Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return "—";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "—";
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    },
    formatDay(dateStr) {
      if (!dateStr) return "";
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-IN", { weekday: "short" });
    },
  },
};
</script>

<style scoped>
/* Main Page Wrapper */
.statement-page-wrapper {
  padding: 12px 6px 40px 6px;
  max-width: 1600px;
  margin: 0 auto;
  font-family: 'Plus Jakarta Sans', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: #0f172a;
}

/* Header Card */
.statement-header-card {
  background: #ffffff !important;
  border: 1px solid #f1ece5 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) !important;
}

.back-nav-btn {
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
}

.header-divider-v {
  width: 1px;
  height: 28px;
  background-color: #e2e8f0;
}

.header-icon-box {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #fff7ee;
  border: 1px solid #faecd8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.page-title {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: 0.5px;
}

.page-subtitle {
  font-size: 11.5px;
  color: #64748b;
  margin-top: 1px;
}

.action-primary-btn {
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 12.5px !important;
  letter-spacing: 0.2px !important;
}

.action-secondary-btn {
  border-radius: 8px !important;
  font-weight: 600 !important;
  font-size: 12px !important;
  border-color: #cbd5e1 !important;
  background: #ffffff !important;
}

.refresh-icon-btn {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
}

/* Customer Hero Card */
.customer-hero-card {
  background: linear-gradient(135deg, #ffffff 0%, #fffbf5 100%) !important;
  border: 1px solid #faecd8 !important;
  box-shadow: 0 2px 6px rgba(154, 107, 39, 0.04) !important;
}

.customer-hero-avatar {
  width: 54px;
  height: 54px;
  border-radius: 14px;
  background: linear-gradient(135deg, #faecd8 0%, #fae4cb 100%);
  border: 1.5px solid #ebd9c2;
  color: #9a6b27;
  font-size: 20px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(154, 107, 39, 0.1);
  flex-shrink: 0;
}

.hero-customer-name {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.customer-status-pill {
  font-size: 10px;
  font-weight: 700;
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
  padding: 2px 8px;
  border-radius: 100px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.kyc-verified-pill {
  font-size: 11px;
  font-weight: 600;
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
  padding: 2px 8px;
  border-radius: 100px;
  display: inline-flex;
  align-items: center;
}

.hero-meta-row {
  font-size: 12px;
  color: #475569;
}

.hero-meta-item {
  display: inline-flex;
  align-items: center;
}

.standing-badge {
  font-size: 12px;
  font-weight: 700;
  padding: 4px 12px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  letter-spacing: 0.2px;
}

.standing-due {
  background: #fff7ed;
  color: #c2410c;
  border: 1px solid #fed7aa;
}

.standing-settled {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.statement-timestamp {
  font-size: 11px;
  color: #64748b;
}

/* 4 KPI Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

@media (max-width: 1100px) {
  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-card {
  background: #ffffff;
  border: 1px solid #f1ece5;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.04);
}

.kpi-highlight-due {
  border-color: #fed7aa;
  background: linear-gradient(135deg, #ffffff 0%, #fffaf5 100%);
}

.kpi-label {
  font-size: 10.5px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.6px;
  text-transform: uppercase;
}

.kpi-value {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
  margin-top: 2px;
  letter-spacing: -0.02em;
}

.kpi-hint {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.kpi-icon-pill {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.text-amber-gold {
  color: #b45309 !important;
}

.text-emerald-green {
  color: #059669 !important;
}

/* Master Table Card */
.statement-table-card {
  background: #ffffff !important;
  border: 1px solid #f1ece5 !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03) !important;
}

.table-toolbar {
  background: #fafaf9;
  border-bottom: 1px solid #f1ece5;
}

.filter-pill-btn {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 12px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.18s ease;
}

.filter-pill-btn:hover {
  background: #fff7ee;
  color: #9a6b27;
  border-color: #faecd8;
}

.filter-pill-btn.active {
  background: #fff7ee;
  color: #9a6b27;
  border-color: #9a6b27;
  box-shadow: 0 1px 3px rgba(154, 107, 39, 0.12);
}

.statement-search-wrap {
  width: 260px;
}

.statement-search-input :deep(.v-input__slot) {
  min-height: 36px !important;
  border-radius: 8px !important;
  font-size: 12.5px !important;
  background: #ffffff !important;
}

.sort-toggle-btn {
  border-radius: 8px !important;
  font-size: 11.5px !important;
  font-weight: 600 !important;
  background: #ffffff !important;
}

/* Table Design */
.statement-ledger-table {
  width: 100%;
}

.statement-ledger-table thead tr th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 11.5px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 10px 16px !important;
}

.statement-row {
  transition: background-color 0.15s ease;
}

.statement-row:hover {
  background-color: #fffdfa !important;
}

.statement-ledger-table tbody tr td {
  padding: 12px 16px !important;
  font-size: 13px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.date-main {
  font-weight: 600;
  color: #0f172a;
}

.date-sub {
  font-size: 10.5px;
  color: #94a3b8;
}

/* Badges */
.txn-type-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.type-bill {
  background: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
}

.type-payment {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
}

.bill-ref-link {
  color: #9a6b27;
  text-decoration: none;
  font-weight: 700;
  background: #fff7ee;
  border: 1px solid #faecd8;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
}

.bill-ref-link:hover {
  background: #faecd8;
  color: #78350f;
}

.bill-ref-plain {
  color: #334155;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
}

.amount-debit {
  color: #b45309;
  font-size: 13.5px;
}

.amount-credit {
  color: #059669;
  font-size: 13.5px;
}

.bill-pending-badge {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.bill-settled-badge {
  color: #059669;
  font-size: 11.5px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
}

.running-balance-text {
  font-size: 13.5px;
}

.balance-positive {
  color: #78350f;
}

.balance-zero {
  color: #64748b;
}

.table-action-icon-btn {
  border: 1px solid #faecd8;
  background: #fff7ee;
  border-radius: 6px;
}

.table-action-icon-btn:hover {
  background: #fae4cb;
}

/* Table Footer Grand Totals */
.statement-total-row {
  background: #faf7f2 !important;
  border-top: 2px solid #e7d8c4 !important;
}

.statement-total-row td {
  padding: 14px 16px !important;
}

.total-label {
  font-size: 12px;
  color: #78350f;
  letter-spacing: 0.5px;
}

/* Empty State */
.empty-icon-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.empty-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
  max-width: 440px;
  margin-left: auto;
  margin-right: auto;
}

/* Print Styles */
.print-only {
  display: none;
}

@media print {
  .no-print {
    display: none !important;
  }

  .statement-page-wrapper {
    padding: 0 !important;
    max-width: 100% !important;
  }

  .print-only {
    display: block !important;
  }

  .statement-header-card,
  .table-toolbar,
  .kpi-grid,
  .customer-hero-card {
    display: none !important;
  }

  .statement-table-card {
    border: none !important;
    box-shadow: none !important;
  }

  .statement-ledger-table {
    border-collapse: collapse !important;
    width: 100% !important;
  }

  .statement-ledger-table th,
  .statement-ledger-table td {
    border: 1px solid #cccccc !important;
    padding: 8px !important;
    font-size: 10pt !important;
  }

  .print-brand-title {
    font-family: serif;
    font-size: 22pt;
    font-weight: bold;
    letter-spacing: 2px;
  }

  .print-brand-tagline {
    font-size: 8pt;
    color: #555555;
    letter-spacing: 1px;
  }

  .print-doc-title {
    font-size: 14pt;
    font-weight: bold;
    color: #333333;
  }

  .print-customer-box {
    border: 1px solid #dddddd;
    background: #fdfdfd;
    border-radius: 4px;
  }

  .print-label {
    font-size: 8pt;
    font-weight: bold;
    color: #666666;
  }

  .print-val-strong {
    font-size: 12pt;
    font-weight: bold;
  }

  .print-val-sub {
    font-size: 9pt;
    color: #444444;
  }

  .border-b-2 {
    border-bottom: 2px solid #333333;
  }

  .print-sign-line {
    width: 200px;
    border-bottom: 1px solid #000000;
    margin-bottom: 6px;
  }

  .print-sign-label {
    font-size: 9pt;
    color: #444444;
  }

  .print-footer-notice {
    font-size: 8pt;
    color: #777777;
  }
}
</style>
